import React, { useEffect, useState } from "react";
import { Sparkles, FlaskConical, TrendingUp, TrendingDown, Flag, CheckCircle2, Play, Tag, PenLine, Lightbulb, Loader2, Undo2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { STEPS } from "@/data/steps";
import { bandColor } from "@/components/ScoreRadial";
import api from "@/lib/api";

const ICON = { created: Lightbulb, decision: PenLine, analysis: Sparkles, update: PenLine, evidence: FlaskConical, evidence_removed: Undo2, move_started: Play, move_completed: CheckCircle2, maturity: Tag };
const TONE = { created: "#7c3aed", decision: "#64748b", analysis: "#7c3aed", update: "#3b82f6", evidence: "#16a34a", evidence_removed: "#f97316", move_started: "#0ea5e9", move_completed: "#16a34a", maturity: "#f97316" };

function fmt(s, vars) { return Object.entries(vars).reduce((acc, [k, v]) => acc.replace(`{${k}}`, v ?? ""), s); }

function ScoreDelta({ score, t }) {
  if (!score || score.new == null) return null;
  const delta = score.previous != null ? score.new - score.previous : 0;
  const Icon = delta >= 0 ? TrendingUp : TrendingDown;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-0.5 bg-[#f4f6fa]" style={{ color: delta > 0 ? "#16a34a" : delta < 0 ? "#f97316" : "#64748b" }}>
      <Icon size={12} /> {score.previous != null && delta !== 0 ? fmt(t("timeline.score_up"), { a: score.previous, b: score.new }) : fmt(t("timeline.score_same"), { b: score.new })}
    </span>
  );
}

function label(it, t, lang) {
  switch (it.kind) {
    case "created": return it.title ? fmt(t("timeline.created_named"), { t: it.title.slice(0, 90) }) : t("timeline.created");
    case "decision": { const s = STEPS.find((x) => x.key === it.step); return fmt(t("timeline.decision"), { step: s ? s.label[lang] : it.step }); }
    case "analysis": return fmt(t("timeline.analysis"), { n: it.score?.new });
    case "update": return fmt(t("timeline.update"), { t: it.title });
    case "evidence": return `${fmt(t("timeline.evidence"), { type: t(`evidence.types.${it.type}`) })} ${fmt(t("timeline.evidence_area"), { area: t(`evidence.areas.${it.area}`) })}`;
    case "evidence_removed": return fmt(t("timeline.evidence_removed"), { area: t(`evidence.areas.${it.area}`) });
    case "move_started": return fmt(t("timeline.move_started"), { t: it.title });
    case "move_completed": return fmt(t("timeline.move_completed"), { t: it.title });
    case "maturity": return fmt(t("timeline.maturity"), { m: t(`maturity.${it.maturity}`) });
    default: return it.title || "";
  }
}

export function Timeline({ projectId }) {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/projects/${projectId}/timeline`).then((r) => setData(r.data)).catch(() => setData({ items: [] })); }, [projectId]);

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#7c3aed]" /></div>;
  const items = data.items || [];
  return (
    <section className="rounded-3xl border border-slate-200 p-5 sm:p-7" data-testid="timeline" aria-labelledby="timeline-title">
      <h3 id="timeline-title" className="cpenz-overline">{t("timeline.title")}</h3>
      <p className="mt-2 text-sm text-slate-600">{t("timeline.sub")}</p>
      {items.length === 0 ? (
        <p className="mt-8 text-slate-500" data-testid="timeline-empty">{t("timeline.empty")}</p>
      ) : (
        <ol className="mt-6 sm:mt-8 relative border-l border-slate-200 ml-3 space-y-5 sm:space-y-7" aria-label={t("timeline.title")}>
          {items.map((it, i) => {
            const Icon = ICON[it.kind] || Flag;
            const color = TONE[it.kind] || "#64748b";
            return (
              <li key={i} className="pl-6 sm:pl-8 relative" data-testid={`timeline-item-${it.kind}`}>
                <span aria-hidden="true" className="absolute -left-[13px] top-0.5 h-6 w-6 rounded-full bg-white border flex items-center justify-center" style={{ borderColor: color, color }}><Icon size={12} /></span>
                <time dateTime={it.date} className="text-[11px] font-semibold tracking-wider uppercase text-slate-500">{new Date(it.date).toLocaleDateString(lang === "IT" ? "it-IT" : "en-GB", { day: "numeric", month: "long", year: "numeric" })}</time>
                <p className="mt-1 text-[15px] text-[#0b0f1a] leading-snug">{label(it, t, lang)}</p>
                {it.kind === "evidence" && it.title && <p className="mt-1 text-sm font-semibold text-slate-700">«{it.title}»</p>}
                {it.detail && <p className="mt-1 text-sm text-slate-600 leading-relaxed">{it.detail}</p>}
                {(it.kind === "evidence" || it.kind === "evidence_removed" || it.kind === "update" || it.kind === "analysis") && it.score && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {it.kind !== "analysis" && <ScoreDelta score={it.score} t={t} />}
                    {it.band && <span className="text-xs font-semibold" style={{ color: bandColor(it.band) }}>{it.band}</span>}
                  </div>
                )}
                {it.score?.reason && it.kind !== "analysis" && <p className="mt-1.5 text-xs text-slate-600 leading-relaxed"><span className="font-semibold text-[#7c3aed] uppercase tracking-wider text-[10px] mr-1">{t("timeline.because")}</span>{it.score.reason}</p>}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
